import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLocale } from "@/contexts/LocaleContext";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Building2, KeyRound } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&h=1200&q=80";

export default function Home() {
  const { t } = useLocale();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/properties", { params: { limit: 6 } })
      .then(({ data }) => setFeatured(data))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-neutral-900">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/25 to-transparent" />
        </div>
        <div className="mx-auto max-w-7xl px-6 md:px-10 pt-24 md:pt-36 pb-32 md:pb-48 text-primary-foreground">
          <div className="label-eyebrow text-primary-foreground/80 mb-6" data-testid="hero-eyebrow">
            {t("hero.eyebrow")}
          </div>
          <h1
            className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-4xl whitespace-pre-line"
            data-testid="hero-title"
          >
            {t("hero.title")}
          </h1>
          <p className="mt-8 max-w-xl text-base md:text-lg text-primary-foreground/85 leading-relaxed">
            {t("hero.sub")}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full btn-hover-lift bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="hero-cta-browse">
              <Link to="/properties">
                {t("hero.cta_browse")} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-primary-foreground/40 text-primary-foreground bg-primary-foreground/5 hover:bg-primary-foreground/15 backdrop-blur" data-testid="hero-cta-publish">
              <Link to="/register">{t("hero.cta_publish")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 pt-20 md:pt-28">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div className="max-w-xl">
            <div className="label-eyebrow text-secondary">01 · {t("hero.eyebrow")}</div>
            <h2 className="mt-2 font-serif text-4xl md:text-5xl">{t("home.featured")}</h2>
            <p className="mt-3 text-muted-foreground">{t("home.featured_sub")}</p>
          </div>
          <Link to="/properties" className="text-sm text-primary hover:underline underline-offset-4" data-testid="see-all-properties">
            {t("nav.properties")} →
          </Link>
        </div>
        {featured.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground text-sm">{t("common.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {featured.map((p, i) => (
              <PropertyCard key={p.property_id} property={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* ROLES */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 pt-28 md:pt-40">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          <div>
            <div className="label-eyebrow text-secondary">02 · Plataforma</div>
            <h2 className="mt-2 font-serif text-4xl md:text-5xl leading-[1.05]">{t("home.why_title")}</h2>
            <p className="mt-4 text-muted-foreground max-w-md">{t("home.why_sub")}</p>
            <Button asChild size="lg" className="rounded-full mt-8 btn-hover-lift" data-testid="roles-cta-signup">
              <Link to="/register">{t("home.cta_signup")}</Link>
            </Button>
          </div>
          <div className="grid gap-6">
            <div className="border border-border p-8 md:p-10 hover:bg-accent/40 transition-colors">
              <KeyRound className="h-6 w-6 text-secondary" />
              <h3 className="mt-4 font-serif text-2xl">{t("home.role_buscador_title")}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t("home.role_buscador_desc")}</p>
            </div>
            <div className="border border-border p-8 md:p-10 hover:bg-accent/40 transition-colors">
              <Building2 className="h-6 w-6 text-secondary" />
              <h3 className="mt-4 font-serif text-2xl">{t("home.role_agente_title")}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t("home.role_agente_desc")}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
